import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";
import { Chip } from "@/components/StatusChip";
import { channelMeta } from "@/components/ChannelBadge";
import { StrategyBuilder, TONE_META } from "@/components/communications/StrategyBuilder";
import { Plus, Pencil, Trash2, Star, Users, ArrowRight, GitBranch } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const StepChain = ({ steps }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid="strategy-step-chain">
    {steps.map((s, i) => {
      const m = channelMeta(s.channel);
      const Icon = m.icon;
      return (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && (
            <span className="flex items-center gap-0.5 text-[9.5px] text-[#555]">
              {s.wait_hours}h <ArrowRight size={9} />
            </span>
          )}
          <span className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium"
            style={{ background: m.bg, color: m.color }} title={`${m.label} · ${s.tone} tone · after ${s.wait_hours}h`}>
            <Icon size={10} /> {m.label}
            <span className="rounded px-1 text-[8.5px] uppercase tracking-wide"
              style={{ background: TONE_META[s.tone]?.bg, color: TONE_META[s.tone]?.color }}>{s.tone}</span>
          </span>
        </span>
      );
    })}
  </div>
);

export const Strategies = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // null closed | "new" | strategy object
  const editParam = searchParams.get("edit");

  const load = useCallback(() => {
    api.get("/strategies").then((r) => setData(r.data)).catch(() => toast.error("Could not load strategies"));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editParam && data) {
      const s = data.strategies.find((x) => x.id === editParam);
      if (s) setEditing(s);
    }
  }, [editParam, data]);

  const closeBuilder = () => {
    setEditing(null);
    if (editParam) setSearchParams({ tab: "strategies" }, { replace: true });
  };

  const onSaved = () => { closeBuilder(); load(); };

  const setDefault = async (s) => {
    try {
      await api.patch(`/strategies/${s.id}`, { is_default: true });
      toast.success(`"${s.name}" is now the default strategy`);
      load();
    } catch {
      toast.error("Could not set default");
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/strategies/${s.id}`);
      toast.success(`Strategy "${s.name}" deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="strategies-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight"><GitBranch size={15} className="text-[#7FAEF9]" /> Communication strategies</h2>
          <p className="text-[11.5px] text-[#A3A3A3]">Escalation playbooks Aria follows when chasing receipts and answers — string channels, waits and tone together.</p>
        </div>
        <button onClick={() => setEditing("new")} data-testid="strategy-new-button"
          className="ml-auto flex h-8 items-center gap-1.5 rounded-lg btn-gradient px-3.5 text-[12.5px] font-medium text-white">
          <Plus size={13} /> New strategy
        </button>
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            <StrategyBuilder strategy={editing === "new" ? null : editing} onSaved={onSaved} onCancel={closeBuilder} />
          </motion.div>
        )}
      </AnimatePresence>

      {!data ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-44 rounded-[14px] bg-[#141414]" />)}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="strategies-grid">
          {data.strategies.map((s) => (
            <motion.div key={s.id} whileHover={{ y: -2 }} transition={{ type: "spring", stiffness: 420, damping: 28 }}
              data-testid={`strategy-card-${s.id}`}
              data-aria-key={`strategy:${s.id}`} data-aria-label={`Strategy: ${s.name}`}
              className="card-lift flex flex-col rounded-[14px] border border-[#222] bg-[#111111] p-5 card-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="truncate text-[13.5px] font-medium">{s.name}</div>
                  {s.is_default && <Chip tone="blue" testId={`strategy-default-badge-${s.id}`}>Default</Chip>}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {!s.is_default && (
                    <button onClick={() => setDefault(s)} data-testid={`strategy-set-default-${s.id}`} title="Set as default"
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-[#6b6b6b] hover:bg-[#1c1c1c] hover:text-[#FBBF24]">
                      <Star size={12} />
                    </button>
                  )}
                  <button onClick={() => setEditing(s)} data-testid={`strategy-edit-${s.id}`} title="Edit strategy"
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-[#6b6b6b] hover:bg-[#1c1c1c] hover:text-white">
                    <Pencil size={12} />
                  </button>
                  {!s.is_default && (
                    <button onClick={() => remove(s)} data-testid={`strategy-delete-${s.id}`} title="Delete strategy"
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-[#6b6b6b] hover:bg-[#1c1c1c] hover:text-[#F87171]">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
              {s.description && <p className="mt-1.5 line-clamp-2 text-[11.5px] leading-relaxed text-[#A3A3A3]">{s.description}</p>}
              <div className="mt-3"><StepChain steps={s.steps} /></div>
              <div className="mt-auto flex items-center justify-between border-t border-[#1c1c1c] pt-2.5 text-[10.5px] text-[#6b6b6b]">
                <span className="flex items-center gap-1"><Users size={10} /> {s.assigned_count} employee{s.assigned_count === 1 ? "" : "s"}</span>
                <span>{s.steps.length} step{s.steps.length === 1 ? "" : "s"}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
